import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef } from 'react';

type OutsideClickDisplay = NonNullable<CSSProperties['display']>;

interface OutsideClickHandlerProps {
  children: ReactNode;
  onOutsideClick: (event: MouseEvent) => void;
  disabled?: boolean;
  display?: OutsideClickDisplay;
  useCapture?: boolean;
}

export function OutsideClickHandler({
  children,
  onOutsideClick,
  disabled = false,
  display = 'block',
  useCapture = true,
}: OutsideClickHandlerProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const onOutsideClickRef = useRef(onOutsideClick);
  onOutsideClickRef.current = onOutsideClick;

  useEffect(() => {
    if (disabled) return;

    const options: AddEventListenerOptions = { capture: useCapture };
    let removeMouseUp: (() => void) | null = null;

    const isOutside = (target: EventTarget | null) =>
      wrapperRef.current !== null &&
      target instanceof Node &&
      !wrapperRef.current.contains(target);

    const handleMouseUp = (event: MouseEvent) => {
      removeMouseUp?.();
      removeMouseUp = null;
      if (isOutside(event.target)) {
        onOutsideClickRef.current(event);
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (!isOutside(event.target)) return;
      document.addEventListener('mouseup', handleMouseUp, options);
      removeMouseUp = () =>
        document.removeEventListener('mouseup', handleMouseUp, options);
    };

    document.addEventListener('mousedown', handleMouseDown, options);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, options);
      removeMouseUp?.();
    };
  }, [disabled, useCapture]);

  return (
    <div ref={wrapperRef} style={display === 'block' ? undefined : { display }}>
      {children}
    </div>
  );
}
